import { useState, useEffect } from 'react';

import { Row, Col, Typography } from 'antd';

import {
  StyledModal,
  SectionWrapper,
  BasicFormItemList,
  FullWidthSpace,
  WithLoading,
  Form,
  CustomNotification,
} from 'common/components';
import AddAssetPimModal from 'pages/product-full-view/components/modal/add-asset-pim-modal';

import { useGetQaPackagingFormDefine } from 'pages/qa-spec/components/qa-packaging/hooks';
import {
  useGetQaPackagingTabData,
  useSaveQaPackagingTab,
} from 'pages/qa-spec/components/qa-packaging/queries';
import { useCheckPermissionOR } from 'hooks/useCheckPermissions';
import { ABILITY_ACTION, ABILITY_SUBJECT } from 'static/Permission';
import {
  getCertificateParams,
  parsePackagingData,
} from 'pages/qa-spec/components/qa-packaging/utils';

import { useSafeProductIdParam } from 'hooks/useProductFullView';

const ProductPackagingModal = (props) => {
  const [formInst] = Form.useForm();
  const checkPermission = useCheckPermissionOR();
  const hasQaSpecPackingEditPermission = checkPermission([
    [ABILITY_ACTION.EDIT, ABILITY_SUBJECT.QA_SPECIFICATION],
  ]);

  const { visible, closeModal, isQASpecLocked, productFull } = props;

  const [uploadModalTitle, setUploadModalTitle] = useState('');
  const [uploadDocumentModalVisible, setUploadDocumentModalVisible] =
    useState(false);

  const checkEdit = hasQaSpecPackingEditPermission && !isQASpecLocked;

  const { id } = useSafeProductIdParam();

  const { data, isLoading: loading } = useGetQaPackagingTabData(
    { productId: id },
    { enabled: !!visible, retry: false }
  );

  const {
    formProductDefine,
    formLeftBottomDefine,
    formProductClaimFirstSectionDefine,
    formRightSecondSection,
    formDietaryThirdSectionDefine,
    formRightFourthSectionDefine,
    formFacilityCertificationsSectionDefine
  } = useGetQaPackagingFormDefine({
    handleToggleUploadDocumentModal: () => {
      handleToggleUploadDocumentModal();
    },
    setUploadModalTitle,
    productFull,
  });
  const handleSaveQaPackagingTab = useSaveQaPackagingTab();
  const { isLoading: saveLoading } = handleSaveQaPackagingTab;

  const handleToggleUploadDocumentModal = () => {
    setUploadDocumentModalVisible((prev) => !prev);
  };

  const handleSave = async (toggleEditMode) => {
    formInst
      .validateFields()
      .then(async (values) => {
        const certificateParams = getCertificateParams(values);

        const formData = { ...values, ...certificateParams };

        handleSaveQaPackagingTab.mutate(
          {
            productId: id,
            formData: formData,
          },
          {
            onSuccess: () => {
              closeModal && closeModal();
            },
          }
        );
      })
      .catch((e) => {
        if (e?.errorFields?.length > 0) {
          CustomNotification.error('Please resolve errors.');
        }
      });
  };
  const onCancel = () => {
    const parsedData = parsePackagingData(data);
    formInst.setFieldsValue(parsedData);
    closeModal && closeModal();
  };

  const onOk = () => {
    handleSave();
  };

  const onFormChange = (changedValues, allValues) => {
    if (
      changedValues.hasOwnProperty('recyclable') &&
      (changedValues?.recyclable === 'No' || !changedValues?.recyclable)
    ) {
      formInst.setFieldsValue({ recyclableTypes: [] });
    }
    if (
      changedValues.hasOwnProperty('bilingual') &&
      (changedValues?.bilingual === 'No' || !changedValues?.bilingual)
    ) {
      formInst.setFieldsValue({ secondaryLanguage: [] });
    }
  };

  useEffect(() => {
    const parsedData = parsePackagingData(data);
    formInst.setFieldsValue(parsedData);
  }, [JSON.stringify(data)]);

  return (
    <StyledModal
      title={'Product Labeling'}
      visible={visible}
      width={1200}
      bodyStyle={{ height: '80vh' }}
      onCancel={onCancel}
      okButtonProps={{ disabled: !checkEdit }}
      maskClosable={false}
      onOk={onOk}
    >
      <WithLoading loading={loading || saveLoading}>
        <div className='scroller--y'>
          <Form
            className='qa-packaging'
            form={formInst}
            style={{ paddingRight: 5 }}
            layout='vertical'
            onValuesChange={onFormChange}
          >
            <Row gutter={[20, 20]}>
              <Col span={12}>
                <FullWidthSpace size={15}>
                  <SectionWrapper title='Product'>
                    <BasicFormItemList
                      formDefine={formProductDefine}
                      style={{ paddingTop: 8 }}
                      labelFn={labelFn}
                      allowEdit={checkEdit}
                    />
                  </SectionWrapper>

                  <BasicFormItemList
                    formDefine={formLeftBottomDefine}
                    labelFn={labelFn}
                    allowEdit={checkEdit}
                  />
                </FullWidthSpace>
              </Col>

              <Col span={12}>
                <FullWidthSpace size={15}>
                  <SectionWrapper title='Product Claims'>
                    <BasicFormItemList
                      formDefine={formProductClaimFirstSectionDefine}
                      style={{ paddingTop: 8 }}
                      labelFn={labelFn}
                      allowEdit={checkEdit}
                    />
                  </SectionWrapper>

                  <BasicFormItemList
                    formDefine={formRightSecondSection}
                    labelFn={labelFn}
                    allowEdit={checkEdit}
                  />

                  <SectionWrapper title='Dietary Claims'>
                    <BasicFormItemList
                      formDefine={formDietaryThirdSectionDefine}
                      style={{ paddingTop: 20 }}
                      labelFn={labelFn}
                      allowEdit={checkEdit}
                    />
                  </SectionWrapper>

                  <SectionWrapper title='Facility Certifications'>
                    <BasicFormItemList
                      formDefine={formFacilityCertificationsSectionDefine}
                      style={{ paddingTop: 20 }}
                      labelFn={labelFn}
                      allowEdit={checkEdit}
                    />
                  </SectionWrapper>

                  <BasicFormItemList
                    formDefine={formRightFourthSectionDefine}
                    labelFn={labelFn}
                    allowEdit={checkEdit}
                  />
                </FullWidthSpace>
              </Col>
            </Row>
            {uploadDocumentModalVisible && (
              <AddAssetPimModal
                onCloseModal={handleToggleUploadDocumentModal}
                addType={'Document'}
                getImagery={[]}
                qaSpec={true}
                getDocument={handleToggleUploadDocumentModal}
                title={uploadModalTitle}
                noCapitalize={true}
              />
            )}
          </Form>
        </div>
      </WithLoading>
    </StyledModal>
  );
};

const labelFn = (labelText) => {
  return <Typography.Text strong>{labelText && labelText}</Typography.Text>;
};

export default ProductPackagingModal;
